import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Card, message, Space } from 'antd'
import {
  CloudDownloadOutlined,
  DiffOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useQuery } from '@tanstack/react-query'

import { uploadToS3 } from 'enrollment-platform/pages/PlanManagementV2/utils/uploadToS3'
import {
  analyzeCompareData,
  exportCompareReport,
  getCompareCounts,
  getCompareData,
  presignCompareDataUpload,
  type AnalyzeCompareResult,
  type CsvCompareRow,
} from '../api/compare-data-api'
import type { CompareRun } from '../api/compare-run-api'
import { dataTypeLabel, type SurecoCsvDataType } from '../constants'
import { useCompareRun } from '../hooks/useCompareRun'
import {
  buildFriendlySchemaError,
  formatDisplayValue,
  getFieldError,
  isDiffField,
  isSchemaMismatchError,
} from '../utils/compareDataHelpers'
import { isSurecoCsvDataType } from '../utils/ideonCompareHelpers'
import UploadAnalyzeCsvModal, {
  type UploadAnalyzeCsvParams,
} from './UploadAnalyzeCsvModal'
import {
  CompareFilterBar,
  CompareResultsToolbar,
  canSearchHios,
} from './CompareFilterBar'
import { COMPARE_STATUSES, compareStatusLabel } from './StatusBadge'
import SummaryCard from './SummaryCard'
import EmptyCompareState from './EmptyCompareState'
import { CompareRunProgress } from './CompareRunProgress'
import {
  CompareResultsGrid,
  type CompareGridPageRequest,
} from './CompareResultsGrid'
import DiffCellRenderer from './DiffCellRenderer'
import styles from '../PlanManagementV3.module.scss'

const COUNT_FIELDS = [
  'totalCsvRows',
  'newCount',
  'modifiedCount',
  'unchangedCount',
  'errorCount',
] as const

// A COMPLETED CSV run's summary is the analyze result (counts for one file).
function isAnalyzeSummary(
  summary: unknown
): summary is Omit<AnalyzeCompareResult, 'runId'> {
  if (typeof summary !== 'object' || summary === null) return false
  const fields = new Map(Object.entries(summary))
  return (
    isSurecoCsvDataType(fields.get('dataType')) &&
    typeof fields.get('year') === 'number' &&
    typeof fields.get('originalS3Key') === 'string' &&
    COUNT_FIELDS.every((key) => typeof fields.get(key) === 'number')
  )
}

function toAnalyzeResult(run: CompareRun): AnalyzeCompareResult | null {
  return isAnalyzeSummary(run.summary)
    ? { ...run.summary, runId: run.runId }
    : null
}

// A FAILED CSV run keeps its rejected rows when the file had no valid rows;
// summary.errorCount says how many can be exported as the error report.
function failedErrorCount(run: CompareRun | null): number {
  const count = run?.summary?.errorCount
  return typeof count === 'number' ? count : 0
}

type CsvStatusFilter = 'NEW' | 'MODIFIED' | 'ERROR'

const STATUS_FILTERS: {
  status: CsvStatusFilter
  countKey: 'newCount' | 'modifiedCount' | 'errorCount'
}[] = [
  { status: 'NEW', countKey: 'newCount' },
  { status: 'MODIFIED', countKey: 'modifiedCount' },
  { status: 'ERROR', countKey: 'errorCount' },
]

type CsvCounts = {
  newCount: number
  modifiedCount: number
  errorCount: number
  unchangedCount: number
  total: number
}

// Counts for the selected states (empty = the whole run). Stored rows are
// counted per state by the API; unchanged rows come from the run summary.
function countsForStates(
  result: AnalyzeCompareResult,
  byState: Record<string, Omit<CsvCounts, 'unchangedCount' | 'total'>>,
  states: string[]
): CsvCounts {
  if (states.length === 0) {
    return {
      newCount: result.newCount,
      modifiedCount: result.modifiedCount,
      errorCount: result.errorCount,
      unchangedCount: result.unchangedCount,
      total: result.totalCsvRows,
    }
  }
  const counts = states.reduce(
    (sum, state) => ({
      newCount: sum.newCount + (byState[state]?.newCount ?? 0),
      modifiedCount: sum.modifiedCount + (byState[state]?.modifiedCount ?? 0),
      errorCount: sum.errorCount + (byState[state]?.errorCount ?? 0),
      unchangedCount:
        sum.unchangedCount + (result.unchangedByState?.[state] ?? 0),
    }),
    { newCount: 0, modifiedCount: 0, errorCount: 0, unchangedCount: 0 }
  )
  return {
    ...counts,
    total:
      counts.newCount +
      counts.modifiedCount +
      counts.errorCount +
      counts.unchangedCount,
  }
}

// Grid callbacks that don't depend on state — module-level so they're stable.
const csvFieldPath = (key: string) => `fieldCompare.${key}.csvValue`
const csvCellText = (row: CsvCompareRow, key: string) =>
  formatDisplayValue(row.fieldCompare[key]?.csvValue)
const csvCellClassRules = (key: string) => ({
  [styles.diffCellHighlight]: (params: { data?: CsvCompareRow }) =>
    !!params.data &&
    csvCellText(params.data, key) === '' &&
    isDiffField(params.data, key),
  // Missing-value validation errors (e.g. "Missing required field") render
  // an empty cell, so give it a visible red-tinted background rather than
  // relying on the (invisible) red error text alone.
  [styles.diffCellErrorHighlight]: (params: { data?: CsvCompareRow }) =>
    !!params.data &&
    csvCellText(params.data, key) === '' &&
    !!getFieldError(params.data, key),
})
const renderCsvCell = (row: CsvCompareRow, key: string) => (
  <DiffCellRenderer row={row} fieldKey={key} />
)

export function CompareDataTab() {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [analyzeResult, setAnalyzeResult] =
    useState<AnalyzeCompareResult | null>(null)
  const [isDownloadingReport, setIsDownloadingReport] = useState(false)
  const [completedAt, setCompletedAt] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<CsvStatusFilter | null>(null)
  const [states, setStates] = useState<string[]>([])
  const [hiosSearch, setHiosSearch] = useState('')
  // Total rows of the grid's current query — the only count that reflects
  // the HIOS search.
  const [gridTotal, setGridTotal] = useState<number | null>(null)
  // Data type of the last analyze started, for messages about a failed start
  // (before any run exists to read it from).
  const lastDataTypeRef = useRef<SurecoCsvDataType | null>(null)

  // Same run lifecycle as Compare Ideon: start → poll → complete / fail,
  // resumed on refresh, cancellable.
  const run = useCompareRun({
    source: 'CSV',
    onCompleted: (completed) => {
      const result = toAnalyzeResult(completed)
      if (!result) {
        message.error('Analyze finished, but its result could not be read.')
        return
      }
      setAnalyzeResult(result)
      setCompletedAt(completed.completedAt ?? dayjs().toISOString())
    },
  })

  const { start: startRun } = run
  const handleAnalyze = useCallback(
    ({ dataType, file }: UploadAnalyzeCsvParams) => {
      lastDataTypeRef.current = dataType
      setAnalyzeResult(null)
      setStatusFilter(null)
      setStates([])
      setHiosSearch('')

      // A step that throws shows its message (see useCompareRun.start).
      void startRun(async () => {
        const { folder, uploadUrl } = await presignCompareDataUpload({
          originalFilename: file.name,
          fileSize: file.size,
          dataType,
        })
        const uploadResult = await uploadToS3({
          presignedUrl: uploadUrl,
          file,
          contentType: 'text/csv',
        })
        if (!uploadResult.success) {
          throw uploadResult.error ?? new Error('S3 upload failed')
        }
        return analyzeCompareData({
          folder,
          originalFilename: file.name,
          dataType,
        })
      })
    },
    [startRun]
  )

  // Failure shown in the alert — from the run (job failed) or the start step.
  const failureMessage = run.error
  const failedDataType = isSurecoCsvDataType(run.failedRun?.dataType)
    ? run.failedRun?.dataType
    : lastDataTypeRef.current
  const isSchemaMismatch =
    !!failureMessage && isSchemaMismatchError(failureMessage)
  const failedRunErrorCount = failedErrorCount(run.failedRun)

  const search =
    analyzeResult && canSearchHios(analyzeResult.dataType) ? hiosSearch : ''

  // Per-state row counts, so the state filter is arithmetic on the cards.
  const countsQuery = useQuery({
    queryKey: [
      'csvCompareCounts',
      analyzeResult?.runId,
      analyzeResult?.dataType,
    ],
    queryFn: () =>
      analyzeResult
        ? getCompareCounts({
            runId: analyzeResult.runId,
            dataType: analyzeResult.dataType,
          })
        : null,
    enabled: !!analyzeResult,
    staleTime: Infinity,
  })
  const byState = countsQuery.data?.byState
  const stateCodes = useMemo(
    () =>
      Object.keys({ ...byState, ...analyzeResult?.unchangedByState }).filter(
        Boolean
      ),
    [byState, analyzeResult]
  )
  const counts = analyzeResult
    ? countsForStates(analyzeResult, byState ?? {}, states)
    : null
  const statusCountKey = STATUS_FILTERS.find(
    (f) => f.status === statusFilter
  )?.countKey
  const visibleCount = !counts
    ? null
    : statusCountKey
      ? counts[statusCountKey]
      : counts.newCount + counts.modifiedCount + counts.errorCount

  const fetchPage = useCallback(
    async (request: CompareGridPageRequest) => {
      if (!analyzeResult) return { items: [], total: 0 }
      const page = await getCompareData({
        ...request,
        runId: analyzeResult.runId,
        dataType: analyzeResult.dataType,
        compareStatus: statusFilter ?? undefined,
        states,
        hiosSearch: search || undefined,
      })
      setGridTotal(page.total)
      return page
    },
    [analyzeResult, statusFilter, states, search]
  )
  const gridKey = `${analyzeResult?.runId}:${statusFilter ?? 'all'}:${states.join(',')}:${search}`
  useEffect(() => setGridTotal(null), [gridKey])

  // The report for what the grid shows (all of it when unfiltered); the
  // failure alert asks for just the rejected rows.
  const handleDownloadReport = useCallback(
    async (params: {
      runId: string
      dataType: SurecoCsvDataType
      compareStatus?: string
      states?: string[]
      hiosSearch?: string
    }) => {
      setIsDownloadingReport(true)
      try {
        await exportCompareReport(params)
      } catch (err) {
        console.error('Failed to download compare report', err)
        message.error('Failed to download the report.')
      } finally {
        setIsDownloadingReport(false)
      }
    },
    []
  )

  const changedCount = analyzeResult
    ? analyzeResult.newCount + analyzeResult.modifiedCount
    : 0
  const hasAnyDifferences = analyzeResult
    ? changedCount + analyzeResult.errorCount > 0
    : false

  return (
    <div className={styles.compareTabRoot}>
      <div className={`${styles.cardHeader} ${styles.compareHeader}`}>
        <h1 className={styles.cardHeaderTitle}>Compare Data</h1>
        {analyzeResult && (
          <Space wrap align="center" size={12}>
            <span className={styles.metaLabel}>
              <strong>File Type:</strong>{' '}
              {dataTypeLabel(analyzeResult.dataType)}
            </span>

            {completedAt && (
              <span className={styles.metaLabel}>
                <strong>Analysis Time:</strong>{' '}
                {dayjs(completedAt).format('MMMM D, YYYY h:mm A')}
              </span>
            )}

            <Button
              className={styles.dsButton}
              icon={<CloudDownloadOutlined />}
              loading={isDownloadingReport}
              disabled={isDownloadingReport}
              onClick={() =>
                void handleDownloadReport({
                  runId: analyzeResult.runId,
                  dataType: analyzeResult.dataType,
                  compareStatus: statusFilter ?? undefined,
                  states,
                  hiosSearch: search || undefined,
                })
              }
            >
              Download report
            </Button>

            <Button
              type="primary"
              icon={<UploadOutlined />}
              loading={run.isRunning}
              disabled={run.isRunning}
              className={styles.uploadAnalyzeButton}
              onClick={() => setIsUploadModalOpen(true)}
            >
              Upload Analyze File
            </Button>
          </Space>
        )}
      </div>
      <Card>
        {failureMessage && (
          <Alert
            type={isSchemaMismatch ? 'warning' : 'error'}
            message={
              isSchemaMismatch ? 'Incorrect file type' : 'Analysis failed'
            }
            description={
              isSchemaMismatch
                ? buildFriendlySchemaError(
                    failureMessage,
                    failedDataType ?? undefined
                  )
                : failureMessage
            }
            closable
            onClose={run.clearError}
            action={
              !isSchemaMismatch &&
              run.failedRun &&
              failedDataType &&
              failedRunErrorCount > 0 ? (
                <Button
                  danger
                  size="small"
                  icon={<CloudDownloadOutlined />}
                  loading={isDownloadingReport}
                  onClick={() =>
                    run.failedRun &&
                    void handleDownloadReport({
                      runId: run.failedRun.runId,
                      dataType: failedDataType,
                      compareStatus: 'ERROR',
                    })
                  }
                >
                  Download error report
                </Button>
              ) : undefined
            }
            className={styles.compareAlert}
          />
        )}

        {analyzeResult && counts ? (
          <>
            {hasAnyDifferences && (
              <CompareFilterBar
                stateCodes={stateCodes}
                states={states}
                onStatesChange={(next) => {
                  setStatusFilter(null)
                  setStates(next)
                }}
                hiosSearch={search}
                onHiosSearchChange={setHiosSearch}
                showHiosSearch={canSearchHios(analyzeResult.dataType)}
              />
            )}
            <div className={styles.summaryRow}>
              {STATUS_FILTERS.map((f) => (
                <SummaryCard
                  key={f.status}
                  label={compareStatusLabel(f.status)}
                  value={counts[f.countKey]}
                  accentColor={COMPARE_STATUSES[f.status]?.color}
                  active={statusFilter === f.status}
                  onClick={
                    hasAnyDifferences
                      ? () =>
                          setStatusFilter((prev) =>
                            prev === f.status ? null : f.status
                          )
                      : undefined
                  }
                />
              ))}
              <SummaryCard label="Unchanged" value={counts.unchangedCount} />
              <SummaryCard label="Total" value={counts.total} />
            </div>
          </>
        ) : run.isRunning ? (
          <CompareRunProgress
            phase={run.phase}
            progress={run.progress}
            stage={run.stage}
            description="This may take a moment for larger files."
            canCancel={run.canCancel}
            onCancel={run.cancel}
          />
        ) : (
          <EmptyCompareState onUpload={() => setIsUploadModalOpen(true)} />
        )}

        <UploadAnalyzeCsvModal
          open={isUploadModalOpen}
          isAnalyzing={run.isRunning}
          onClose={() => setIsUploadModalOpen(false)}
          onAnalyze={handleAnalyze}
        />

        {analyzeResult && counts && hasAnyDifferences && (
          <CompareResultsToolbar
            statusLabel={statusFilter ? compareStatusLabel(statusFilter) : null}
            count={search ? gridTotal : visibleCount}
            onShowAll={() => setStatusFilter(null)}
          />
        )}

        {analyzeResult && hasAnyDifferences && (
          <CompareResultsGrid
            key={gridKey}
            dataType={analyzeResult.dataType}
            fetchPage={fetchPage}
            fieldPath={csvFieldPath}
            cellText={csvCellText}
            cellClassRules={csvCellClassRules}
            renderCell={renderCsvCell}
          />
        )}

        {analyzeResult && !hasAnyDifferences && (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>
              <DiffOutlined />
            </div>
            <h2 className={styles.emptyStateTitle}>No differences found</h2>
            <p className={styles.emptyStateDescription}>
              Every row in the CSV matches the live data — there&apos;s nothing
              to review.
            </p>
          </div>
        )}
      </Card>
    </div>
  )
}
